import { useState } from 'react';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { acceptBooking, declineBooking } from '@/lib/booking-api';
import type { BookingResponse } from '@/lib/schemas/booking';
import { ChatPanel } from '@/components/ChatPanel';
import { X, Video, Calendar, Clock, MessageSquare, Check, AlertCircle } from 'lucide-react';

interface TutorBookingDetailPanelProps {
  booking: BookingResponse;
  onClose: () => void;
  onUpdate: (updated: BookingResponse) => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border border-amber-200/50 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/30',
  accepted: 'bg-emerald-50 text-emerald-700 border border-emerald-200/50 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/30',
  declined: 'bg-rose-50 text-rose-700 border border-rose-200/50 dark:bg-rose-950/20 dark:text-rose-400 dark:border-rose-900/30',
  cancelled: 'bg-slate-50 text-slate-500 border border-slate-200/50 dark:bg-slate-800/40 dark:text-slate-400 dark:border-slate-850/30',
  completed: 'bg-blue-50 text-blue-700 border border-blue-200/50 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900/30',
};

function formatDate(isoStart: string): string {
  const start = new Date(isoStart);
  return start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(isoStart: string, isoEnd: string): string {
  const start = new Date(isoStart);
  const end = new Date(isoEnd);
  const startTime = start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const endTime = end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${startTime} – ${endTime}`;
}

export function TutorBookingDetailPanel({ booking: b, onClose, onUpdate }: TutorBookingDetailPanelProps) {
  const { accessToken } = useAuth();
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [videoLink, setVideoLink] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [tutorNotes, setTutorNotes] = useState('');
  const [chatOpen, setChatOpen] = useState(false);

  async function handleAccept() {
    if (!accessToken || !videoLink.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await acceptBooking(accessToken, b.id, { videoLink: videoLink.trim() });
      onUpdate(updated);
      setAcceptOpen(false);
      setVideoLink('');
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'Failed to accept booking');
    } finally {
      setBusy(false);
    }
  }

  async function handleDecline() {
    if (!accessToken) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await declineBooking(accessToken, b.id, {
        tutorNotes: tutorNotes.trim() || undefined,
      });
      onUpdate(updated);
      setDeclineOpen(false);
      setTutorNotes('');
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'Failed to decline booking');
    } finally {
      setBusy(false);
    }
  }

  const dateStr = formatDate(b.slotStartUtc);
  const timeStr = formatTime(b.slotStartUtc, b.slotEndUtc);

  return (
    <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[450px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-l border-slate-200 dark:border-slate-800 shadow-2xl overflow-y-auto z-50 flex flex-col transition-all duration-300">
      {/* Header */}
      <div className="sticky top-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-100 dark:border-slate-800/85 p-4 flex items-center justify-between z-20">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-extrabold shadow-sm">
            {b.classTitle ? b.classTitle.charAt(0).toUpperCase() : 'B'}
          </div>
          <div>
            <h3 className="font-extrabold text-slate-900 dark:text-white leading-tight text-sm truncate max-w-[250px]">
              {b.classTitle || b.subject || 'Interactive Session'}
            </h3>
            <p className="text-[9px] font-bold text-slate-400 dark:text-slate-505 uppercase tracking-widest mt-0.5">
              Class Details
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="h-8 w-8 rounded-full flex items-center justify-center bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors cursor-pointer border border-slate-100/50 dark:border-slate-700/50"
        >
          <X size={16} className="text-slate-600 dark:text-slate-400" />
        </button>
      </div>

      {/* Content */}
      <div className="p-5 space-y-6 flex-1">
        {/* Status */}
        <div className="flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/25 p-3 rounded-2xl border border-slate-100 dark:border-slate-800/60">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Status</span>
          <span className={`inline-flex rounded-full px-3 py-0.5 text-xs font-bold ${STATUS_COLORS[b.status] ?? ''}`}>
            {STATUS_LABELS[b.status] ?? b.status}
          </span>
        </div>

        {/* Info Grid Card */}
        <div className="bg-slate-50/40 dark:bg-slate-800/20 p-4.5 rounded-2xl border border-slate-200/50 dark:border-slate-800/40 space-y-4 shadow-xs">
          {/* Date & Time */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2.5 text-xs font-bold text-slate-600 dark:text-slate-350">
              <Calendar className="w-4 h-4 text-blue-500 shrink-0" />
              <span>{dateStr}</span>
            </div>
            <div className="flex items-center gap-2.5 text-xs font-bold text-slate-600 dark:text-slate-350">
              <Clock className="w-4 h-4 text-violet-500 shrink-0" />
              <span>{timeStr}</span>
            </div>
          </div>

          <div className="h-px bg-slate-200/60 dark:bg-slate-800/60" />

          {/* Subject */}
          {b.subject && (
            <div className="space-y-1">
              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-505 uppercase tracking-wider">Subject</p>
              <p className="text-xs font-extrabold text-slate-800 dark:text-slate-200">{b.subject}</p>
            </div>
          )}

          {/* Class Type */}
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-505 uppercase tracking-wider">Class Type</p>
            <p className="text-xs font-extrabold text-slate-800 dark:text-slate-200 uppercase">{b.type} class</p>
          </div>
        </div>

        {/* Parent Notes */}
        {b.parentNotes && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-505 uppercase tracking-wider">Parent Notes</p>
            <p className="text-xs italic text-slate-600 dark:text-slate-350 bg-slate-50/50 dark:bg-slate-800/40 p-3 rounded-xl border-l-2 border-brand-500 leading-relaxed dark:border-brand-500">
              {b.parentNotes}
            </p>
          </div>
        )}

        {/* Tutor Notes */}
        {b.tutorNotes && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-505 uppercase tracking-wider">Your Notes / Reason</p>
            <p className="text-xs text-slate-600 dark:text-slate-350 bg-slate-50/50 dark:bg-slate-800/40 p-3 rounded-xl border-l-2 border-indigo-500 leading-relaxed dark:border-indigo-500">
              {b.tutorNotes}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="border-t border-slate-100 dark:border-slate-800 pt-5 space-y-3">
          {actionError && (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-red-650 dark:text-red-400">
              <AlertCircle size={14} />
              <span>{actionError}</span>
            </div>
          )}

          {b.status === 'pending' && (
            <div className="space-y-3">
              {!acceptOpen && !declineOpen && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setAcceptOpen(true)}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 px-4 py-2.5 text-xs font-bold text-white shadow-sm transition-all hover:scale-[1.02] cursor-pointer"
                  >
                    <Check size={16} />
                    Accept Invite
                  </button>
                  <button
                    onClick={() => setDeclineOpen(true)}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-xs font-bold text-red-650 hover:bg-red-50 dark:border-red-900/50 dark:bg-slate-800 dark:text-red-400 dark:hover:bg-red-950/30 transition-all cursor-pointer"
                  >
                    Decline
                  </button>
                </div>
              )}

              {acceptOpen && (
                <div className="space-y-3 p-3 bg-slate-50/50 dark:bg-slate-800/25 rounded-2xl border border-slate-100 dark:border-slate-800">
                  <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Accept Class Invite</p>
                  <input
                    type="url"
                    value={videoLink}
                    onChange={(e) => setVideoLink(e.target.value)}
                    placeholder="Enter Zoom or Google Meet link..."
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-emerald-500"
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => void handleAccept()}
                      disabled={busy || !videoLink.trim()}
                      className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1.5 text-xs font-bold disabled:opacity-50 cursor-pointer"
                    >
                      {busy ? 'Saving...' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => { setAcceptOpen(false); setVideoLink(''); }}
                      className="rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-3.5 py-1.5 text-xs font-bold cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {declineOpen && (
                <div className="space-y-3 p-3 bg-slate-50/50 dark:bg-slate-800/25 rounded-2xl border border-slate-100 dark:border-slate-800">
                  <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Decline Class Invite</p>
                  <textarea
                    value={tutorNotes}
                    onChange={(e) => setTutorNotes(e.target.value)}
                    placeholder="Reason for declining (optional)..."
                    rows={3}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-red-500"
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => void handleDecline()}
                      disabled={busy}
                      className="rounded-lg bg-red-650 hover:bg-red-700 text-white px-3.5 py-1.5 text-xs font-bold disabled:opacity-50 cursor-pointer"
                    >
                      {busy ? 'Saving...' : 'Confirm Decline'}
                    </button>
                    <button
                      onClick={() => { setDeclineOpen(false); setTutorNotes(''); }}
                      className="rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-3.5 py-1.5 text-xs font-bold cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {b.status === 'accepted' && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
              {b.videoLink && (
                <a
                  href={b.videoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 px-2.5 py-2.5 text-[11px] font-extrabold uppercase tracking-wider text-white hover:shadow-md hover:shadow-blue-500/10 transition-all cursor-pointer active:scale-95 duration-200 whitespace-nowrap"
                >
                  <Video className="w-3.5 h-3.5 shrink-0" />
                  <span>Join Class</span>
                </a>
              )}

              <button
                onClick={() => setChatOpen(!chatOpen)}
                className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl bg-indigo-50 hover:bg-indigo-100/80 text-indigo-700 border border-indigo-200/60 dark:bg-indigo-950/30 dark:hover:bg-indigo-950/50 dark:text-indigo-300 dark:border-indigo-900/50 px-2.5 py-2.5 text-[11px] font-extrabold uppercase tracking-wider transition-all cursor-pointer active:scale-95 duration-200 whitespace-nowrap"
              >
                <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                <span>Chat</span>
              </button>
            </div>
          )}
        </div>

        {/* Chat */}
        {chatOpen && b.status === 'accepted' && accessToken && (
          <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
            <ChatPanel bookingId={b.id} accessToken={accessToken} open={chatOpen} onOpenChange={setChatOpen} />
          </div>
        )}
      </div>
    </div>
  );
}
